import React, { useMemo } from 'react';

import { GLOBAL, useTranslate } from '@lumapps/translations';

import { useNavigationAdminConfiguration } from '../../context/NavigationAdminConfigurationContext';
import { useNavigationAdminVisibleByReference } from '../../context/NavigationAdminVisibleByReferenceContext';
import { useNavigationListFilters, useNavigationListFiltersDispatch } from '../../context/NavigationListFiltersContext';
import { actions } from '../../ducks/navigationAdminFilterSlice';
import { NavigationListFilterSelect, NavigationListFilterSelectChoice } from './NavigationListFilterSelect';

export interface GroupFilterProps {
    label?: string;
}

/**
 * Select component to select a user group to filter the navigation items to.
 */
export const GroupFilter: React.FC<GroupFilterProps> = ({ label }) => {
    const { pluralize, translateKey } = useTranslate();
    const { filters } = useNavigationListFilters();
    const visibleByGroupReferences = useNavigationAdminVisibleByReference();
    const { defaultFeeds } = useNavigationAdminConfiguration();
    const dispatch = useNavigationListFiltersDispatch();

    /** Build the choices from all the group used in the navigation nodes  */
    const choices = React.useMemo<Array<NavigationListFilterSelectChoice>>(() => {
        /** First add the "public" and "all" groups  */
        const defaultFeedsValues = [];
        const { publicFeed, allFeed } = defaultFeeds || {};
        const publicFeedId = publicFeed?.id;
        const allFeedId = allFeed?.id;

        if (allFeedId) {
            defaultFeedsValues.push({ id: allFeed.id, name: allFeed.name });
        }
        if (publicFeedId) {
            defaultFeedsValues.push({ id: publicFeed.id, name: publicFeed.name });
        }

        /** Create an array of object containing each group Id and group name. */
        const choicesWithoutDefault = visibleByGroupReferences
            ? Object.keys(visibleByGroupReferences)
                  // Remove public feeds as they were manually added.
                  .filter((choiceId) => choiceId !== publicFeedId && choiceId !== allFeedId)
                  .map((choiceId) => ({
                      id: choiceId,
                      name: visibleByGroupReferences[choiceId],
                  }))
            : [];

        /** return an array with the default feeds at the start. */
        return [...defaultFeedsValues, ...choicesWithoutDefault];
    }, [defaultFeeds, visibleByGroupReferences]);

    const handleSelect = React.useCallback(
        (newValue) => {
            dispatch(
                actions.setFilter({
                    filter: 'group',
                    value: newValue?.id,
                }),
            );
        },
        [dispatch],
    );

    const selectedChoice = useMemo<NavigationListFilterSelectChoice | undefined>(
        () => (filters?.group ? choices.find(({ id }) => id === filters.group) : undefined),
        [choices, filters],
    );

    return (
        <NavigationListFilterSelect
            label={label || translateKey(GLOBAL.SHOW_AS)}
            // Hard code a count to use the plural form of "groups"
            placeholder={pluralize(GLOBAL.GROUP, 2)}
            choices={choices}
            selected={selectedChoice}
            onSelect={handleSelect}
            dataAttributeElement="filter-by-group"
        />
    );
};
